import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Calendar, DollarSign } from "lucide-react";
import { useDespachos } from "@/hooks/useDespachos";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

interface PeriodicPayment {
    id: string;
    concept: string;
    amount: number;
    frequency: string;
    dueDate: string;
    status: "active" | "inactive";
}

export const ClientPeriodicPayments = ({ client }: { client: any }) => {
    const { updateClient } = useDespachos();
    const [isOpen, setIsOpen] = useState(false);
    const [concept, setConcept] = useState("");
    const [amount, setAmount] = useState("");
    const [frequency, setFrequency] = useState("Mensual");
    const [dueDate, setDueDate] = useState("");

    if (!client) {
        return (
            <Card>
                <CardContent className="p-8 text-center text-gray-500">Cargando...</CardContent>
            </Card>
        );
    }

    // Obtener los datos del address serializado
    let extraData: any = {};
    if (client.address) {
        try {
            extraData = JSON.parse(client.address);
        } catch (e) {
            extraData = {};
        }
    }

    const paymentsList: PeriodicPayment[] = extraData.payments || [];

    const handleCreatePayment = async () => {
        if (!concept.trim() || !amount.trim()) return;

        const newPayment: PeriodicPayment = {
            id: Math.random().toString(36).substring(2, 9),
            concept: concept.trim(),
            amount: parseFloat(amount) || 0,
            frequency,
            dueDate,
            status: "active"
        };

        const updatedExtra = {
            ...extraData,
            payments: [...paymentsList, newPayment]
        };

        await updateClient(client.id, {
            address: JSON.stringify(updatedExtra)
        });

        // Limpiar
        setConcept("");
        setAmount("");
        setDueDate("");
        setIsOpen(false);
    };

    const handleDeletePayment = async (paymentId: string) => {
        const updatedPayments = paymentsList.filter(p => p.id !== paymentId);
        const updatedExtra = {
            ...extraData,
            payments: updatedPayments
        };

        await updateClient(client.id, {
            address: JSON.stringify(updatedExtra)
        });
    };

    // Formateador
    const formatCurrency = (val: number) => {
        return new Intl.NumberFormat("es-CO", {
            style: "currency",
            currency: "COP",
            minimumFractionDigits: 0
        }).format(val);
    };

    return (
        <Card className="border border-gray-100/80 shadow-sm overflow-hidden bg-white">
            <CardHeader className="flex flex-row items-center justify-between pb-3 bg-gray-50/50 border-b border-gray-100">
                <div>
                    <CardTitle className="text-base font-bold text-gray-900">Pagos Periódicos (Igualas)</CardTitle>
                    <p className="text-xs text-gray-400">Mensualidades, abonos de honorarios o suscripciones del cliente</p>
                </div>
                <Dialog open={isOpen} onOpenChange={setIsOpen}>
                    <DialogTrigger asChild>
                        <Button size="sm" className="rounded-xl bg-legal-blue hover:bg-legal-navy gap-1.5 shadow-md">
                            <Plus className="h-4 w-4" /> Configurar Pago
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-2xl">
                        <DialogHeader>
                            <DialogTitle className="text-gray-900 font-bold">Configurar Pago Periódico</DialogTitle>
                            <p className="text-xs text-gray-400">Pacta una cuota recurrente para este cliente</p>
                        </DialogHeader>
                        <div className="py-4 space-y-4">
                            <div className="space-y-2">
                                <Label>Concepto de Cobro *</Label>
                                <Input className="rounded-xl" placeholder="Ej: Honorarios Mensuales Asesoría" value={concept} onChange={e => setConcept(e.target.value)} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Valor Mensual *</Label>
                                    <Input className="rounded-xl" placeholder="$0.00" value={amount} onChange={e => setAmount(e.target.value)} />
                                </div>
                                <div className="space-y-2">
                                    <Label>Fecha Próximo Vence</Label>
                                    <Input type="date" className="rounded-xl" value={dueDate} onChange={e => setDueDate(e.target.value)} />
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="ghost" className="rounded-xl" onClick={() => setIsOpen(false)}>Cancelar</Button>
                            <Button className="rounded-xl bg-legal-blue" onClick={handleCreatePayment} disabled={!concept.trim() || !amount.trim()}>Configurar</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </CardHeader>
            <CardContent className="p-0">
                {paymentsList.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow className="border-gray-100 hover:bg-transparent">
                                <TableHead className="font-semibold text-gray-500">Concepto</TableHead>
                                <TableHead className="font-semibold text-gray-500">Frecuencia</TableHead>
                                <TableHead className="font-semibold text-gray-500">Vencimiento</TableHead>
                                <TableHead className="font-semibold text-gray-500">Estado</TableHead>
                                <TableHead className="font-semibold text-gray-500 text-right">Valor</TableHead>
                                <TableHead className="font-semibold text-gray-500 text-right">Acción</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {paymentsList.map((p) => (
                                <TableRow key={p.id} className="border-gray-100 hover:bg-blue-50/20 transition-colors">
                                    <TableCell className="font-bold text-gray-800 text-xs">{p.concept}</TableCell>
                                    <TableCell className="text-gray-500 text-xs font-semibold">{p.frequency || 'Mensual'}</TableCell>
                                    <TableCell className="text-gray-500 text-xs">{p.dueDate ? new Date(p.dueDate).toLocaleDateString() : '—'}</TableCell>
                                    <TableCell>
                                        <span className="text-[10px] font-bold py-0.5 px-2 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
                                            Activo
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-right font-bold text-gray-900 text-xs">
                                        {formatCurrency(p.amount)}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => handleDeletePayment(p.id)}
                                            className="h-8 w-8 p-0 rounded-lg text-red-500 hover:text-red-600 hover:bg-red-50"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <div className="text-center py-16 text-gray-400">
                        <Calendar className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                        <p className="text-sm">No hay cuotas o pagos periódicos configurados.</p>
                        <p className="text-xs text-gray-300 mt-1">Configura cobros mensuales automáticos usando el botón superior.</p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
